import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  type TypographyProps,
} from '@mui/material';
import { Show, SideNavViewAllButton, useIsMobile } from '@side/pantry';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useSafeInteractionParams } from '@/common/hooks/useSafeInteractionParams';

type Props = {
  extraHeaderContent?: ReactNode;
  Icon: ReactNode;
  children: ReactNode;
  subtitle?: string | null;
  title?: string | null;
  headerSx?: SxProps<Theme>;
  titleProps?: TypographyProps;
};

export const BaseSideNavigation = ({
  extraHeaderContent,
  Icon,
  children,
  subtitle,
  title,
  titleProps,
  headerSx,
}: Props) => {
  const isMobile = useIsMobile('md');
  const { basePath } = useRouter();
  const { interactionType } = useSafeInteractionParams();

  return (
    <Stack
      sx={{
        justifyContent: 'space-between',
        gap: { xs: 3, md: 0 },
        height: '100%',
      }}
    >
      <Stack
        sx={{
          gap: 2.5,
          pt: { xs: 2, md: 3 },
          pb: { xs: 0, md: 3 },
          px: { xs: 0, md: 2 },
          height: '100%',
        }}
      >
        <Show when={!isMobile}>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 2,
              borderBottom: 'none',
              borderColor: 'other.outlineBorder',
              ...headerSx,
            }}
          >
            <Avatar
              sx={{
                backgroundColor: 'grey.200',
              }}
            >
              {Icon}
            </Avatar>
            <Stack
              direction="column"
              sx={{
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                component="h1"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
                {...titleProps}
              >
                <Show
                  when={title}
                  fallback={
                    <Skeleton
                      data-testid="generic-side-navigation-title-skeleton"
                      variant="text"
                    />
                  }
                >
                  {title}
                </Show>
              </Typography>
              <Show when={subtitle}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {subtitle}
                </Typography>
              </Show>
              {extraHeaderContent}
            </Stack>
          </Stack>
        </Show>
        <Box>{children}</Box>
      </Stack>
      <SideNavViewAllButton
        LinkComponent={Link}
        interactionType={interactionType}
        basePath={basePath}
        sx={{ pl: { xs: 0, md: 2 } }}
      />
    </Stack>
  );
};
